/**
 * /settings/subscription — Abonnement Stripe
 */

import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { PublicHeader, Breadcrumb } from '@/components/jurisprudence';
import { usePublicAuth } from '@/hooks/usePublicAuth';
import { useStats } from '@/hooks/useStats';
import { SettingsNav } from './SettingsProfile';
import { getDateLocale } from '@/i18n';

const CHECKOUT_BASE = '/api/checkout';

async function apiFetch(path: string, token: string, options: RequestInit = {}) {
  const res = await fetch(`${CHECKOUT_BASE}${path}`, {
    ...options,
    headers: {
      'Content-Type':  'application/json',
      'Authorization': `Bearer ${token}`,
      ...(options.headers || {}),
    },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `HTTP ${res.status}`);
  }
  return res.json();
}

interface SubStatus {
  plan:             string;
  has_subscription: boolean;
  interval:         string | null;
  period_end:       string | null;
  last_invoice_url: string | null;
}

export default function SettingsSubscription() {
  const { t } = useTranslation();
  const navigate                          = useNavigate();
  const [searchParams]                    = useSearchParams();
  const { user, loading, getToken }       = usePublicAuth();
  const { totalStr }                      = useStats();

  const [subStatus,     setSubStatus]     = useState<SubStatus | null>(null);
  const [loadingSub,    setLoadingSub]    = useState(true);
  const [loadingPortal, setLoadingPortal] = useState(false);
  const [interval,      setIntervalVal]   = useState<'month' | 'year'>('month');
  const [error,         setError]         = useState('');
  const [success,       setSuccess]       = useState('');
  const [cancelRequested, setCancelRequested] = useState(false);
  const [loadingCancel,   setLoadingCancel]   = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate('/login?redirect=/settings/subscription', { replace: true });
  }, [user, loading, navigate]);

  // Messages retour Stripe
  useEffect(() => {
    if (searchParams.get('success') === 'true') {
      setSuccess(t('settings.subscription_activated'));
      window.history.replaceState({}, '', '/settings/subscription');
    }
    if (searchParams.get('canceled') === 'true') {
      setError(t('settings.payment_canceled'));
      window.history.replaceState({}, '', '/settings/subscription');
    }
  }, [searchParams]);

  const handleCancelRequest = async () => {
    const token = await getToken();
    if (!token) return;
    setLoadingCancel(true);
    try {
      await apiFetch('/cancel-request', token, { method: 'POST' });
      setCancelRequested(true);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingCancel(false);
    }
  };

  const loadStatus = useCallback(async () => {
    const token = await getToken();
    if (!token) return;
    setLoadingSub(true);
    try {
      const data = await apiFetch('/status', token);
      setSubStatus(data);
      if (data.interval) setIntervalVal(data.interval);
    } catch (e: any) {
      console.warn('subscription status:', e.message);
    } finally {
      setLoadingSub(false);
    }
  }, [getToken]);

  useEffect(() => { if (user) loadStatus(); }, [user, loadStatus]);

  const handleSubscribe = async () => {
    setError('');
    const token = await getToken();
    if (!token) return;
    setLoadingSub(true);
    try {
      const data = await apiFetch('/session', token, {
        method: 'POST',
        body:   JSON.stringify({ interval }),
      });
      window.location.href = data.url;
    } catch (e: any) {
      setError(e.message);
      setLoadingSub(false);
    }
  };

  const handlePortal = async () => {
    setError('');
    const token = await getToken();
    if (!token) return;
    setLoadingPortal(true);
    try {
      const data = await apiFetch('/portal', token, { method: 'POST' });
      window.location.href = data.url;
    } catch (e: any) {
      setError(e.message);
      setLoadingPortal(false);
    }
  };

  const isPaid = subStatus?.plan === 'paid' && subStatus?.has_subscription;
  const periodEndFr = subStatus?.period_end
    ? new Date(subStatus.period_end).toLocaleDateString(getDateLocale(), {
        day: 'numeric', month: 'long', year: 'numeric',
      })
    : null;

  if (loading) return <Spinner />;
  if (!user)   return null;

  return (
    <>
      <Helmet><title>{t('settings.subscription.page_title')}</title></Helmet>
      <div className="min-h-screen bg-background flex flex-col">
        <PublicHeader />
        <Breadcrumb items={[{ label: t('settings.breadcrumb_home'), to: '/' }, { label: t('settings.my_account') }]} />
        <main className="flex-1 max-w-2xl mx-auto w-full px-4 py-10">
          <SettingsNav active="subscription" />

          {error && (
            <div className="mt-4 p-3 rounded-lg bg-destructive/10 border border-destructive/20 text-sm text-destructive">
              {error}
            </div>
          )}
          {success && (
            <div className="mt-4 p-3 rounded-lg bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800 text-sm text-green-700 dark:text-green-400">
              {success}
            </div>
          )}

          <section className="rounded-xl border bg-card p-6 mt-6">
            <div className="flex items-start justify-between mb-2">
              <h2 className="text-base font-semibold">{t('settings.subscription.title')}</h2>
              {!loadingSub && (
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                  isPaid
                    ? 'bg-primary/10 text-primary border border-primary/20'
                    : 'bg-muted text-muted-foreground'
                }`}>
                  {isPaid ? t('settings.subscription.pro_active') : t('common.free')}
                </span>
              )}
            </div>

            {loadingSub ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-6 h-6 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
              </div>
            ) : isPaid ? (
              /* ── Abonné ── */
              <div className="space-y-4 mt-4">
                <div className="p-4 rounded-lg bg-primary/5 border border-primary/15 space-y-3">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-muted-foreground">{t('settings.subscription.plan')}</span>
                    <span className="font-medium">
                      {subStatus?.interval === 'year' ? t('settings.subscription.yearly') : t('settings.subscription.monthly')}
                    </span>
                  </div>
                  {periodEndFr && (
                    <div className="flex items-center justify-between text-sm border-t pt-3">
                      <span className="text-muted-foreground">{t('settings.subscription.next_charge')}</span>
                      <span className="font-medium">{periodEndFr}</span>
                    </div>
                  )}
                  {subStatus?.last_invoice_url && (
                    <div className="flex items-center justify-between text-sm border-t pt-3">
                      <span className="text-muted-foreground">{t('settings.subscription.last_invoice')}</span>
                      <a
                        href={subStatus.last_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline font-medium flex items-center gap-1"
                      >
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                            d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                        </svg>
                        {t('settings.subscription.download_pdf')}
                      </a>
                    </div>
                  )}
                </div>

                <p className="text-sm text-muted-foreground">
                  {t('settings.subscription.portal_description')}
                </p>

                <button
                  onClick={handlePortal}
                  disabled={loadingPortal}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-lg border-2 border-border hover:border-primary/30 hover:bg-accent text-sm font-medium transition-all disabled:opacity-50"
                >
                  {loadingPortal ? (
                    <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                    </svg>
                  )}
                  {t('settings.subscription.manage_portal_all')}
                </button>

                {!cancelRequested ? (
                  <div className="pt-4 border-t">
                    <p className="text-xs text-muted-foreground mb-3">
                      {t('settings.subscription.cancel_prompt')}
                    </p>
                    <button
                      onClick={handleCancelRequest}
                      disabled={loadingCancel}
                      className="text-sm text-muted-foreground hover:text-destructive transition-colors underline underline-offset-2"
                    >
                      {loadingCancel ? t('settings.subscription.cancel_sending') : t('settings.subscription.cancel_request')}
                    </button>
                  </div>
                ) : (
                  <div className="pt-4 border-t p-4 rounded-lg bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800">
                    <p className="text-sm text-amber-700 dark:text-amber-400 font-medium">
                      {t('settings.subscription.cancel_sent')}
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-500 mt-1">
                      {t('settings.subscription.cancel_sent_description')}
                    </p>
                  </div>
                )}
              </div>

            ) : (
              /* ── Non abonné ── */
              <div className="space-y-5 mt-4">
                <p className="text-sm text-muted-foreground">
                  {t('settings.subscription.free_description')}
                </p>

                {/* Toggle mensuel / annuel */}
                <div className="flex items-center gap-1 p-1 rounded-lg bg-muted w-fit">
                  {(['month', 'year'] as const).map(iv => (
                    <button
                      key={iv}
                      onClick={() => setIntervalVal(iv)}
                      className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all flex items-center gap-1.5 ${
                        interval === iv
                          ? 'bg-background shadow text-foreground'
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {iv === 'month' ? t('settings.subscription.monthly') : t('settings.subscription.yearly')}
                      {iv === 'year' && (
                        <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400 px-1.5 py-0.5 rounded font-semibold">
                          -16%
                        </span>
                      )}
                    </button>
                  ))}
                </div>

                {/* Carte prix */}
                <div className="p-5 rounded-xl border bg-muted/30">
                  {interval === 'month' ? (
                    <div className="mb-4">
                      <p className="text-3xl font-bold">
                        {t('settings.subscription.amount_monthly')}<span className="text-base font-normal text-muted-foreground">{t('settings.subscription.per_month')}</span>
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">{t('settings.subscription.cancel_anytime')}</p>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <p className="text-3xl font-bold">
                        {t('settings.subscription.amount_yearly')}<span className="text-base font-normal text-muted-foreground">{t('settings.subscription.per_year')}</span>
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        {t('settings.subscription.yearly_savings')}
                      </p>
                    </div>
                  )}
                  <ul className="space-y-2">
                    {[
                      t('settings.subscription.features.unlimited', { total: totalStr ?? t('settings.subscription.total_fallback') }),
                      t('settings.subscription.features.graph'),
                      t('settings.subscription.features.mcp'),
                      t('settings.subscription.features.fiches'),
                      t('settings.subscription.features.filters'),
                    ].map(f => (
                      <li key={f} className="flex items-center gap-2 text-sm">
                        <svg className="w-3.5 h-3.5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7"/>
                        </svg>
                        {f}
                      </li>
                    ))}
                  </ul>
                </div>

                <button
                  onClick={handleSubscribe}
                  disabled={loadingSub}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-violet-600 text-white font-bold hover:bg-violet-700 disabled:opacity-50 transition-colors shadow-lg shadow-violet-600/20"
                >
                  {loadingSub ? (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                          d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"/>
                      </svg>
                      {interval === 'year' ? t('settings.subscription.subscribe_yearly') : t('settings.subscription.subscribe_monthly')}
                    </>
                  )}
                </button>
                <p className="text-center text-xs text-muted-foreground">
                  {t('settings.subscription.secure_payment')}
                </p>
              </div>
            )}
          </section>
        </main>
      </div>
    </>
  );
}

function Spinner() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="w-8 h-8 border-4 border-primary/30 border-t-primary rounded-full animate-spin" />
    </div>
  );
}